import { useOutletContext } from "react-router-dom";
import { Sparkles, Eye, Heart, MessageCircle, Share2, Bookmark, MousePointerClick, Instagram, Facebook, Linkedin, Youtube, Globe } from "lucide-react";

const PLATFORM_ICONS = { Instagram, Facebook, LinkedIn: Linkedin, YouTube: Youtube, Website: Globe };

export default function PortalAnalytics() {
  const { analytics } = useOutletContext();
  if (!analytics) return <div className="p-8 text-neutral-500">Loading…</div>;
  const totals = analytics.totals;

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px]">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Performance</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Post Analytics</h1>
        <p className="text-neutral-600 text-sm mt-1">Live stats aggregated from all your social platforms</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <TotalBox icon={<Eye className="w-3.5 h-3.5"/>} label="Total Reach" value={totals.reach.toLocaleString()}/>
        <TotalBox icon={<Heart className="w-3.5 h-3.5"/>} label="Likes" value={totals.likes.toLocaleString()}/>
        <TotalBox icon={<MessageCircle className="w-3.5 h-3.5"/>} label="Comments" value={totals.comments.toLocaleString()}/>
        <TotalBox icon={<Share2 className="w-3.5 h-3.5"/>} label="Shares" value={totals.shares.toLocaleString()}/>
        <TotalBox icon={<Bookmark className="w-3.5 h-3.5"/>} label="Saves" value={totals.saves.toLocaleString()}/>
        <TotalBox icon={<MousePointerClick className="w-3.5 h-3.5"/>} label="Clicks" value={totals.clicks.toLocaleString()}/>
        <TotalBox icon={<Eye className="w-3.5 h-3.5"/>} label="Impressions" value={totals.impressions.toLocaleString()}/>
        <TotalBox icon={<Sparkles className="w-3.5 h-3.5"/>} label="Avg Engagement" value={`${analytics.avg_engagement}%`} highlight/>
      </div>

      <div className="panel p-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-orange-400 mb-3">By Platform</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(analytics.by_platform).map(([plat, v]) => {
            const Icon = PLATFORM_ICONS[plat] || Globe;
            return (
              <div key={plat} className="rounded-lg border border-[#E4E7EC] p-4 bg-[#FFFFFF]" data-testid={`platform-${plat.toLowerCase()}`}>
                <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-orange-700"/><div className="text-sm font-semibold">{plat}</div></div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] font-mono">
                  <div><div className="text-neutral-500">Posts</div><div className="text-neutral-900">{v.posts}</div></div>
                  <div><div className="text-neutral-500">Reach</div><div className="text-neutral-900">{v.reach.toLocaleString()}</div></div>
                  <div><div className="text-neutral-500">Eng.</div><div className="text-neutral-900">{v.engagement.toLocaleString()}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="panel">
        <div className="p-4 text-[11px] font-mono uppercase tracking-widest text-orange-400 border-b border-[#E4E7EC]">Per-Post Performance ({analytics.posts.length})</div>
        <div className="divide-y divide-[#E4E7EC]">
          {analytics.posts.length === 0 && <div className="p-6 text-sm text-neutral-500">No live posts yet.</div>}
          {analytics.posts.map(p => {
            const Icon = PLATFORM_ICONS[p.platform] || Globe;
            return (
              <div key={p.id} className="p-4 grid md:grid-cols-6 gap-3 items-center" data-testid={`post-analytics-${p.id}`}>
                <div className="md:col-span-2 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-md bg-[#FFFFFF] border border-[#E4E7EC] grid place-items-center"><Icon className="w-4 h-4 text-orange-700"/></div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.content_topic || p.content_type}</div>
                    <div className="text-[10px] font-mono uppercase text-neutral-500">{p.platform} · {p.publishing_date}</div>
                  </div>
                </div>
                <Mini label="Reach" value={p.stats.reach.toLocaleString()}/>
                <Mini label="Likes" value={p.stats.likes.toLocaleString()}/>
                <Mini label="Comments" value={p.stats.comments.toLocaleString()}/>
                <Mini label="Eng %" value={`${p.stats.engagement_rate}%`} highlight/>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TotalBox({ icon, label, value, highlight }) {
  return (
    <div className="panel p-4">
      <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-neutral-600">{icon}{label}</div>
      <div className={`mt-1 text-xl font-bold ${highlight?"text-emerald-700":""}`}>{value}</div>
    </div>
  );
}
function Mini({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`text-sm font-semibold ${highlight?"text-emerald-700":""}`}>{value}</div>
    </div>
  );
}
